import React, { useState, useEffect } from 'react';
import RWDropdownList from 'react-widgets/lib/DropdownList';
import _get from 'lodash/get';
import striptags from 'striptags';

import { client } from 'cccisd-apollo';
import { Field, CccisdSelect, CccisdFieldWrapper, CccisdInput } from 'cccisd-formik';
import CccisdToggle from 'cccisd-toggle';
import Editor from 'cccisd-wysiwyg';

import style from '../style.css';
import assignmentsQuery from './assignments.graphql';
import surveysQuery from './surveys.graphql';
import dragDropsQuery from './dragDrops.graphql';

const DragDrop = ({ localForm, initialWidget }) => {
    const [assignments, setAssignments] = useState([]);
    const [isLoadingAssignments, setIsLoadingAssignments] = useState(true);
    const [selectedAssignmentId, setSelectedAssignmentId] = useState(initialWidget?.assignmentId || '');

    const [surveys, setSurveys] = useState([]);
    const [isLoadingSurveys, setIsLoadingSurveys] = useState(true);
    const [selectedSurveyId, setSelectedSurveyId] = useState(initialWidget?.surveyId || '');

    const [dragDrops, setDragDrops] = useState({});
    const [isLoadingDragDrops, setIsLoadingDragDrops] = useState(true);

    useEffect(() => {
        async function getAssignments() {
            setIsLoadingAssignments(true);
            const resp = await client.query({
                query: assignmentsQuery,
                fetchPolicy: 'network-only',
            });

            const options = _get(resp, 'data.flows.assignmentList', []) || [];
            if (!options[0] || options[0].assignmentId !== '') {
                options.unshift({ assignmentId: '', label: '-- Select Quest --', disabled: true });
            }
            setAssignments(options);
            setIsLoadingAssignments(false);
        }

        getAssignments();
    }, []);

    useEffect(() => {
        async function getSurveys() {
            setIsLoadingSurveys(true);
            const resp = await client.query({
                query: surveysQuery,
                fetchPolicy: 'network-only',
                variables: {
                    assignmentId: selectedAssignmentId,
                },
            });

            const options = _get(resp, 'data.flows.surveyList', []) || [];
            if (!options[0] || options[0].surveyId !== '') {
                options.unshift({ surveyId: '', label: '-- Select Survey --', disabled: true });
            }

            setSurveys(options);
            setIsLoadingSurveys(false);
        }

        if (selectedAssignmentId) {
            getSurveys();
        }
    }, [selectedAssignmentId]);

    useEffect(() => {
        async function getDragDrops() {
            setIsLoadingDragDrops(true);
            const resp = await client.query({
                query: dragDropsQuery,
                variables: {
                    assignmentId: selectedAssignmentId,
                    surveyId: selectedSurveyId,
                },
                fetchPolicy: 'network-only',
            });

            const dragDropList = {};
            const flows = _get(resp, 'data.flows.flowList', []) || [];
            const metricsKeys = _get(resp, 'data.metrics.keyList', []) || [];

            flows.forEach(f => {
                const entities = _get(f, 'entityList', []) || [];
                entities.forEach(e => {
                    dragDropList[e.entityId] = {
                        sources: e.content.sources,
                        targets: e.content.targets.map(t => ({
                            ...t,
                            reportDisabled: false,
                            reportLabel: striptags(t.label),
                        })),
                    };
                    dragDropList[e.entityId].targets.push({
                        label: 'Unselected',
                        id: -1,
                        reportDisabled: true,
                        reportLabel: 'Unselected',
                    });
                });
            });

            metricsKeys.forEach(mk => {
                // entity must be deleted
                if (!dragDropList[mk.entityId]) {
                    return;
                }

                if (!dragDropList[mk.entityId].title) {
                    dragDropList[mk.entityId].title = mk.title;
                }

                // initialize structure
                if (!Array.isArray(dragDropList[mk.entityId]?.variableIds)) {
                    dragDropList[mk.entityId].variableIds = [];
                    dragDropList[mk.entityId].variableIdReportHeaderMapping = {};
                }

                const varId = mk?.variable?.variableId;
                if (!varId) {
                    return;
                }

                // don't need duration
                if (mk.variable.reportHeader.endsWith('_duration')) {
                    return;
                }

                dragDropList[mk.entityId].variableIds.push(varId);
                dragDropList[mk.entityId].variableIdReportHeaderMapping[varId] = mk.variable.reportHeader;
            });

            setDragDrops(dragDropList);
            setIsLoadingDragDrops(false);
        }

        if (selectedAssignmentId && selectedSurveyId) {
            getDragDrops();
        }
    }, [selectedAssignmentId, surveys, selectedSurveyId]);

    // handle case where survey or assignment has been deleted since report was created
    const isAssignmentValid =
        selectedAssignmentId && assignments.some(opt => Number(opt.assignmentId) === Number(selectedAssignmentId));
    const isSurveyValid = selectedSurveyId && surveys.some(opt => Number(opt.surveyId) === Number(selectedSurveyId));

    return (
        <>
            <Field name="assignmentId">
                {fieldProps => (
                    <CccisdSelect
                        {...fieldProps}
                        label="Quest"
                        options={assignments.map(a => ({
                            value: a.assignmentId,
                            ...a,
                        }))}
                        disabled={isLoadingAssignments}
                        onChange={event => {
                            const newVal = event.currentTarget.value;
                            setSelectedAssignmentId(newVal);
                            setSelectedSurveyId('');
                            localForm.setFieldValue('assignmentId', newVal);
                            localForm.setFieldValue('surveyId', '');
                            localForm.setFieldValue('dragDropEntityId', '');
                            localForm.setFieldValue('dragDrop', {});
                            localForm.setFieldValue('questionText', '');
                        }}
                    />
                )}
            </Field>
            <Field name="surveyId">
                {fieldProps => (
                    <CccisdSelect
                        {...fieldProps}
                        disabled={!localForm.values.assignmentId || !isAssignmentValid || isLoadingSurveys}
                        label="Survey"
                        options={surveys.map(s => ({
                            value: s.surveyId,
                            ...s,
                        }))}
                        onChange={event => {
                            const newVal = event.currentTarget.value;
                            setSelectedSurveyId(newVal);
                            localForm.setFieldValue('surveyId', newVal);
                            localForm.setFieldValue('dragDropEntityId', '');
                            localForm.setFieldValue('dragDrop', {});
                            localForm.setFieldValue('questionText', '');
                        }}
                    />
                )}
            </Field>
            <Field name="dragDropEntityId">
                {fieldProps => (
                    <CccisdFieldWrapper {...fieldProps} label="Drag &amp; Drop">
                        <RWDropdownList
                            data={Object.keys(dragDrops).map(entityId => {
                                return {
                                    entityId,
                                    title: `Drag & Drop ${entityId}: ${dragDrops[entityId].title}`,
                                };
                            })}
                            busy={!!(isAssignmentValid && isSurveyValid && isLoadingDragDrops)}
                            disabled={!isAssignmentValid || !isSurveyValid}
                            autoComplete="on"
                            onChange={val => {
                                localForm.setFieldValue('dragDropEntityId', val.entityId);
                                const dragDropInfo = val.entityId && dragDrops[val.entityId];

                                const defaultQuestionText = _get(dragDropInfo, 'title', '');
                                localForm.setFieldValue('questionText', defaultQuestionText);
                                localForm.setFieldValue('dragDrop', dragDropInfo);
                            }}
                            value={{
                                entityId: localForm.values.dragDropEntityId,
                                title: dragDrops[localForm.values.dragDropEntityId]?.title,
                            }}
                            defaultValue={{
                                entityId: localForm.values.dragDropEntityId,
                                title: dragDrops[localForm.values.dragDropEntityId]?.title,
                            }}
                            dataKey="entityId"
                            textField="title"
                        />
                    </CccisdFieldWrapper>
                )}
            </Field>
            <Field
                name="questionText"
                label="Question Text"
                component={CccisdInput}
                disabled={!localForm.values.dragDropEntityId}
            />
            <Field name="dragDrop">
                {fieldProps => {
                    const targets = _get(fieldProps, 'field.value.targets', []) || [];
                    return (
                        <CccisdFieldWrapper {...fieldProps} label="Targets">
                            {targets.length === 0 ? (
                                <div className={'alert ' + style.noDndSelected} role="alert">
                                    <p>
                                        <i>No Drag &amp; Drop selected</i>
                                    </p>
                                </div>
                            ) : (
                                <div className={style.dndTargets}>
                                    {targets.map(t => (
                                        <div className={style.target} key={t.id}>
                                            <h5>{striptags(t.label)}</h5>
                                            <CccisdToggle
                                                value={!t.reportDisabled}
                                                onChange={() => {
                                                    // have to deep copy so formik recognizes the change
                                                    const curr = t.reportDisabled;
                                                    const newTargets = fieldProps.field.value.targets.map(targ => {
                                                        if (targ.id === t.id) {
                                                            return {
                                                                ...targ,
                                                                reportDisabled: !curr,
                                                            };
                                                        }
                                                        return { ...targ };
                                                    });

                                                    const newDragDropVal = {
                                                        ...fieldProps.field.value,
                                                        targets: newTargets,
                                                    };
                                                    localForm.setFieldValue('dragDrop', newDragDropVal);
                                                }}
                                                label="Show in report?"
                                            />
                                            <div className="form-inline">
                                                <div className={'form-group ' + style.targetLabel}>
                                                    <label htmlFor={`target_${t.id}`}>Label</label>
                                                    <Editor
                                                        type="input"
                                                        className="form-control"
                                                        content={t.reportLabel}
                                                        onChange={newVal => {
                                                            // have to deep copy so formik recognizes the change
                                                            const newTargets = fieldProps.field.value.targets.map(
                                                                targ => {
                                                                    if (targ.id === t.id) {
                                                                        return {
                                                                            ...targ,
                                                                            reportLabel: newVal,
                                                                        };
                                                                    }
                                                                    return { ...targ };
                                                                }
                                                            );

                                                            const newDragDropVal = {
                                                                ...fieldProps.field.value,
                                                                targets: newTargets,
                                                            };
                                                            localForm.setFieldValue('dragDrop', newDragDropVal);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CccisdFieldWrapper>
                    );
                }}
            </Field>
        </>
    );
};

export default DragDrop;
